export const useSecurityQuestionsStore = defineStore(
  "securityQuestions",
  () => {
    // State
    const showSetupModal = ref(false);
    const intendedDestination = ref("");
    const isSetupRequired = ref(false);

    // Raw /status response of the current user, shared by every caller.
    // Plain variables (not state): the in-flight Promise must not be serialized.
    let cachedStatus: { userId: unknown; response: any } | null = null;
    let inFlight: { userId: unknown; promise: Promise<any> } | null = null;

    // Actions
    function loadStatus(userId: unknown, fetcher: () => Promise<any>) {
      if (cachedStatus && cachedStatus.userId === userId) {
        return Promise.resolve(cachedStatus.response);
      }
      if (inFlight && inFlight.userId === userId) return inFlight.promise;
      const request = { userId, promise: null as unknown as Promise<any> };
      request.promise = fetcher()
        .then((response) => {
          // Ignore the answer if the cache was invalidated or the user changed meanwhile.
          if (inFlight === request) cachedStatus = { userId, response };
          return response;
        })
        .finally(() => {
          if (inFlight === request) inFlight = null;
        });
      inFlight = request;
      return request.promise;
    }

    function invalidateStatus() {
      cachedStatus = null;
      inFlight = null;
    }

    function setRequiredSetup(required: boolean, destination?: string) {
      isSetupRequired.value = required;
      showSetupModal.value = required;
      if (destination) {
        intendedDestination.value = destination;
      }
    }

    function completeSetup() {
      showSetupModal.value = false;
      isSetupRequired.value = false;

      // Navigate to intended destination if available
      if (intendedDestination.value) {
        const destination = intendedDestination.value;
        intendedDestination.value = "";
        navigateTo(destination);
      }
    }

    function skipSetup() {
      showSetupModal.value = false;
      isSetupRequired.value = false;
      intendedDestination.value = "";
    }

    function hideModal() {
      showSetupModal.value = false;
    }

    // Getters
    const shouldShowSetup = computed(() => showSetupModal.value);

    return {
      // State
      showSetupModal: readonly(showSetupModal),
      intendedDestination: readonly(intendedDestination),
      isSetupRequired: readonly(isSetupRequired),

      // Getters
      shouldShowSetup,

      // Actions
      loadStatus,
      invalidateStatus,
      setRequiredSetup,
      completeSetup,
      skipSetup,
      hideModal,
    };
  },
);
